import React, { useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import ItemsTable from "@/components/ItemsTable";
import { useDropdownOptions } from "@/hooks/useDropdownOptions";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Link as LinkIcon, Ban, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function SendOffs() {
  const { owners, sendoffTypes, sendoffStatuses, customerOpts, companyOpts } = useDropdownOptions();
  const [manualCopy, setManualCopy] = useState(null); // { url, expiresAt, label }
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const columns = [
    { key: "title", label: "Item", type: "text", required: true, placeholder: "Proof, invoice, pickup notice…" },
    { key: "company", label: "Company", type: "select", options: companyOpts, width: 180 },
    { key: "customer_id", label: "Customer", type: "select", options: customerOpts, width: 200 },
    { key: "item_type", label: "Type", type: "select", options: sendoffTypes, width: 170 },
    { key: "owner", label: "Owner", type: "select", options: owners, width: 140 },
    { key: "status", label: "Status", type: "select", options: sendoffStatuses, width: 160 },
    { key: "file_id", label: "File", type: "file", width: 200 },
    { key: "date", label: "Date", type: "date", width: 150 },
    { key: "tracking_info", label: "Tracking / Ref", type: "text", width: 180 },
    { key: "notes", label: "Notes", type: "text" },
  ];

  const shareLink = async (item, hours, label) => {
    if (!item.file_id) {
      toast.error("Attach a file first before sharing");
      return;
    }
    let data;
    try {
      const res = await api.post(`/send_offs/${item.id}/share`, { expires_hours: hours });
      data = res.data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create share link");
      return;
    }

    // Try programmatic clipboard write. If the browser rejects (Safari/Firefox
    // drop user-activation across the awaited network call), fall back to a
    // dialog so the user can copy the URL manually.
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard api");
      await navigator.clipboard.writeText(data.share_url);
      toast.success(`${label} link copied`, {
        description: `Expires ${new Date(data.expires_at).toLocaleString()}`,
      });
    } catch {
      setManualCopy({
        url: data.share_url,
        expiresAt: data.expires_at,
        label,
      });
    }
  };

  const revokeAll = async (item) => {
    try {
      const { data } = await api.post(`/send_offs/${item.id}/shares/revoke_all`);
      toast.success(
        data.revoked_count
          ? `Revoked ${data.revoked_count} active link${data.revoked_count === 1 ? "" : "s"}`
          : "No active links to revoke",
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke shares");
    }
  };

  const copyFromDialog = async () => {
    // Direct user gesture — clipboard is allowed here.
    try {
      await navigator.clipboard.writeText(manualCopy.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Absolute last resort: select the input so the user can Ctrl/Cmd+C
      inputRef.current?.select();
      toast.info("Select the URL above and press Ctrl/Cmd+C to copy");
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-10">
      <PageHeader
        eyebrow="Customer Send-Off"
        title="Proofs, Invoices & Pickups"
        description="Track everything going out to customers — proofs, invoices, pickup notices, and shipment tracking. Share links expire automatically and can be revoked at any time."
      />
      <ItemsTable
        endpoint="send_offs"
        columns={columns}
        defaultItem={{ title: "", customer_id: "", company: "JTD of CHS", item_type: "proof", status: "ready to send", owner: "", notes: "", tracking_info: "" }}
        testIdPrefix="sendoffs"
        emptyLabel="Nothing queued to send yet."
        rowActions={(item) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                disabled={!item.file_id}
                data-testid={`sendoffs-share-${item.id}`}
                title={item.file_id ? "Share options" : "Attach a file first"}
                className="h-9 w-9 text-muted-foreground hover:text-espresso hover:bg-secondary/60 disabled:opacity-40"
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover">
              <DropdownMenuLabel className="label-caps">Share {item.title}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => shareLink(item, 24, "24-hour")}
                data-testid={`sendoffs-share-${item.id}-24h`}
              >
                <LinkIcon className="h-3.5 w-3.5 mr-2" /> Copy 24-hour link
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => shareLink(item, 72, "72-hour")}
                data-testid={`sendoffs-share-${item.id}-72h`}
              >
                <LinkIcon className="h-3.5 w-3.5 mr-2" /> Copy 72-hour link
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => shareLink(item, 168, "7-day")}
                data-testid={`sendoffs-share-${item.id}-7d`}
              >
                <LinkIcon className="h-3.5 w-3.5 mr-2" /> Copy 7-day link
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => revokeAll(item)}
                data-testid={`sendoffs-revoke-${item.id}`}
                className="text-destructive focus:text-destructive"
              >
                <Ban className="h-3.5 w-3.5 mr-2" /> Revoke all active links
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      {/* Manual-copy fallback dialog (Safari / Firefox where clipboard write
          after an awaited network call is rejected). */}
      <Dialog
        open={!!manualCopy}
        onOpenChange={(open) => { if (!open) { setManualCopy(null); setCopied(false); } }}
      >
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Share link ready
            </DialogTitle>
            <DialogDescription>
              {manualCopy && (
                <>Your browser blocked the automatic copy. The {manualCopy.label} link is live — copy it manually below. Expires {new Date(manualCopy.expiresAt).toLocaleString()}.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              readOnly
              value={manualCopy?.url || ""}
              onFocus={(e) => e.target.select()}
              data-testid="sendoffs-manual-copy-url"
              className="bg-background text-sm font-mono"
            />
            <Button onClick={copyFromDialog} data-testid="sendoffs-manual-copy-btn"
              className="bg-espresso text-sand hover:bg-espresso/90 shrink-0">
              {copied ? <><Check className="h-4 w-4 mr-1.5" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualCopy(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

